import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import {
    QuarterMonth,
    WorkEndDateMode,
    WorkStartDateMode,
} from "@common/types/serviceTemplateRecurrence";

// Shared display format for the schedule and occurrence date cells so both
// stay consistent.
export const SCHEDULE_DATE_FORMAT = "MMM d, yyyy";

export const PERIOD_LABELS: Record<ServiceTemplateRecurrencePeriod, string> = {
    [ServiceTemplateRecurrencePeriod.DAILY]: "Daily",
    [ServiceTemplateRecurrencePeriod.WEEKLY]: "Weekly",
    [ServiceTemplateRecurrencePeriod.MONTHLY]: "Monthly",
    [ServiceTemplateRecurrencePeriod.QUARTERLY]: "Quarterly",
    [ServiceTemplateRecurrencePeriod.ANNUALLY]: "Annually",
};

/** Singular unit labels for the "Every N …" period trigger (matches JE recurrence). */
export const PERIOD_UNIT_LABELS: Record<
    ServiceTemplateRecurrencePeriod,
    string
> = {
    [ServiceTemplateRecurrencePeriod.DAILY]: "Day",
    [ServiceTemplateRecurrencePeriod.WEEKLY]: "Week",
    [ServiceTemplateRecurrencePeriod.MONTHLY]: "Month",
    [ServiceTemplateRecurrencePeriod.QUARTERLY]: "Quarter",
    [ServiceTemplateRecurrencePeriod.ANNUALLY]: "Year",
};

export const SUPPORTED_SCHEDULE_PERIODS: ServiceTemplateRecurrencePeriod[] = [
    ServiceTemplateRecurrencePeriod.DAILY,
    ServiceTemplateRecurrencePeriod.WEEKLY,
    ServiceTemplateRecurrencePeriod.MONTHLY,
    ServiceTemplateRecurrencePeriod.QUARTERLY,
    ServiceTemplateRecurrencePeriod.ANNUALLY,
];

const MONTH_OF_YEAR_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

export const MONTH_OF_YEAR_LABELS: Record<number, string> = Object.fromEntries(
    MONTH_OF_YEAR_OPTIONS.map(({ value, label }) => [value, label]),
);

export const MONTH_OF_YEAR_VALUES: number[] = MONTH_OF_YEAR_OPTIONS.map(
    ({ value }) => value,
);

export const QUARTER_MONTH_OPTIONS: Array<{
    value: QuarterMonth;
    label: string;
}> = [
    { value: QuarterMonth.FIRST, label: "1st month" },
    { value: QuarterMonth.SECOND, label: "2nd month" },
    { value: QuarterMonth.THIRD, label: "3rd month" },
];

export const QUARTER_MONTH_LABELS = {
    [QuarterMonth.FIRST]: "1st month",
    [QuarterMonth.SECOND]: "2nd month",
    [QuarterMonth.THIRD]: "3rd month",
} satisfies Record<QuarterMonth, string>;

/** Sun–Sat toggles for weekly schedules (0–6). */
export const WEEKDAY_TOGGLE_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 0, label: "S" },
    { value: 1, label: "M" },
    { value: 2, label: "T" },
    { value: 3, label: "W" },
    { value: 4, label: "T" },
    { value: 5, label: "F" },
    { value: 6, label: "S" },
];

/** Mon–Fri options for next-weekday work start mode. */
export const WEEKDAY_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
];

export const SUPPORTED_WORK_START_MODES: WorkStartDateMode[] = [
    WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END,
    WorkStartDateMode.NEXT_WEEKDAY_AFTER_SERVICE_END,
];

export const SUPPORTED_WORK_END_MODES: WorkEndDateMode[] = [
    WorkEndDateMode.OFFSET_DAYS_AFTER_WORK_START,
    WorkEndDateMode.RELATIVE_CALENDAR_DATE_AFTER_WORK_START,
];

export const WEEKDAY_LABELS: Record<number, string> = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
};

export function dayOfMonthOrdinal(day: number): string {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) {
        return `${day}th`;
    }
    switch (day % 10) {
        case 1:
            return `${day}st`;
        case 2:
            return `${day}nd`;
        case 3:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
}

export const DAY_OF_MONTH_OPTIONS: Array<{ value: number; label: string }> =
    Array.from({ length: 31 }, (_, index) => {
        const value = index + 1;
        return { value, label: dayOfMonthOrdinal(value) };
    });

export const DAY_OF_MONTH_VALUES: number[] = DAY_OF_MONTH_OPTIONS.map(
    ({ value }) => value,
);
